import { ChangeDetectionStrategy, Component, DestroyRef, inject, ProviderToken, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute } from '@angular/router';
import { connectState } from '@examdojo/angular/util';
import { SyllabusQuery } from '@examdojo/category';
import { ImageChatRealtimeService, provideImageChat } from '@examdojo/chat';
import { ButtonComponent } from '@examdojo/core/button';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { IconComponent } from '@examdojo/core/icon';
import { shareOneReplay } from '@examdojo/core/rxjs';
import { assertNonNullable } from '@examdojo/core/util/assert';
import { isNotNullish } from '@examdojo/core/util/nullish';
import { isAnyEditingAllowed } from '@examdojo/models/question-editor';
import {
  getFormData,
  JsonEditorTriggerDirective,
  NewQuestionItemButtonComponent,
  provideQuestionItemsForm,
  QuestionActiveItemEditorComponent,
  QuestionEntityQuery,
  QuestionEntityService,
  QuestionItemsEditorComponent,
  QuestionValidationHeaderComponent,
} from '@examdojo/question';
import { ReferenceQuestionPopupTriggerDirective, ReferenceQuestionService } from '@examdojo/reference-question';
import {
  DerivativeQuestionReviewType,
  DerivativeQuestionReviewTypes,
  QuestionReviewFeedbackFormState,
  QuestionType,
  ReviewItemService,
  SubmitButtonText,
} from '@examdojo/review';
import { LoaderComponent } from '@examdojo/ui/loader';
import { UserQuery } from '@examdojo/user';
import { AngularSplitModule } from 'angular-split';
import isEqual from 'lodash/isEqual';
import {
  BehaviorSubject,
  distinctUntilChanged,
  EMPTY,
  filter,
  finalize,
  first,
  map,
  merge,
  of,
  skip,
  switchMap,
  tap,
} from 'rxjs';
import { ActiveModeService } from '../../storage/active-mode-storage-manager';
import { ReviewItemImageChatEffectService } from '../effects/review-item-image-chat-effect.service';
import { QuestionItemsCachingService } from '../question-items-caching/question-items-caching.service';
import { QuestionItemsStorageService } from '../question-items-storage-service';
import {
  CreatingReviewItemBackendError,
  QuestionValidationService,
  ReviewConfig,
} from '../question-validation.service';
import { SkipQuestionDialogService } from '../skip-question-dialog/skip-question-dialog.service';
import { DerivativeQuestionEditorItemsFormService } from './derivative-question-editor-items-form-service';
import { getQuestionReviewConfig } from './derivative-question-validation.model';
import { DerivativeQuestionSidebarComponent } from './review-mode-sidebars/derivative-question-sidebar.component';

@Component({
  selector: 'dojo-derivative-question-validation',
  templateUrl: './derivative-question-validation.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AngularSplitModule,
    LoaderComponent,
    QuestionItemsEditorComponent,
    QuestionActiveItemEditorComponent,
    ButtonComponent,
    ReactiveFormsModule,
    FormsModule,
    NewQuestionItemButtonComponent,
    JsonEditorTriggerDirective,
    ReferenceQuestionPopupTriggerDirective,
    MatTooltip,
    MatSlideToggleModule,
    DerivativeQuestionSidebarComponent,
    QuestionValidationHeaderComponent,
    IconComponent,
  ],
  providers: [
    provideQuestionItemsForm(DerivativeQuestionEditorItemsFormService),
    provideImageChat(),
    ReviewItemImageChatEffectService,
    SkipQuestionDialogService,
    QuestionValidationService,
    QuestionItemsStorageService,
    QuestionItemsCachingService,
  ],
  host: {
    class: 'flex flex-col h-full',
  },
})
export class DerivativeQuestionValidationComponent {
  constructor(
    private readonly route: ActivatedRoute,
    private readonly questionValidationService: QuestionValidationService,
    private readonly questionService: QuestionEntityService,
    private readonly questionQuery: QuestionEntityQuery,
    private readonly referenceQuestionService: ReferenceQuestionService,
    private readonly destroyRef: DestroyRef,
    private readonly reviewItemService: ReviewItemService,
    private readonly questionEditorItemsFormService: DerivativeQuestionEditorItemsFormService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly syllabusQuery: SyllabusQuery,
    private readonly skipQuestionDialogService: SkipQuestionDialogService,
    private readonly userQuery: UserQuery,
    private readonly questionItemsStorageService: QuestionItemsStorageService,
    private readonly activeModeService: ActiveModeService,
  ) {
    merge(
      this.fetchQuestionOnIdChange(),
      this.resetFeedbackOnQuestionIdChange(),
      this.handleSyllabusChange(),
      this.handleModeChange(),
    )
      .pipe(takeUntilDestroyed())
      .subscribe();
  }

  private readonly instantiatedServices = [ReviewItemImageChatEffectService, ImageChatRealtimeService].map(
    (service: ProviderToken<unknown>) => inject(service),
  );

  readonly activeQuestionId$ = this.questionValidationService.activeReviewItem$.pipe(
    map((reviewItem) => reviewItem?.question_id),
    distinctUntilChanged(),
  );

  readonly reviewFormState = signal<QuestionReviewFeedbackFormState | null>(null);

  readonly mode$ = this.route.params.pipe(
    map((params) => {
      const mode = params['mode'] as DerivativeQuestionReviewType;
      if (DerivativeQuestionReviewTypes.includes(mode)) {
        return mode;
      }

      return null;
    }),
  );

  readonly storeInBraintrust = signal(false);
  readonly brainTrustDataset = signal('');

  readonly CreatingReviewItemBackendError = CreatingReviewItemBackendError;
  readonly SubmitButtonText = SubmitButtonText;

  readonly activeQuestion$ = this.activeQuestionId$.pipe(
    switchMap((questionId) => (questionId ? this.questionQuery.selectEntity(questionId) : of(null))),
    map((question) => question?.question),
  );

  readonly referenceQuestion$ = this.activeQuestionId$.pipe(
    filter(isNotNullish),
    switchMap((questionId) =>
      this.referenceQuestionService
        .fetchByQuestionId(questionId)
        .pipe(this.errorHandlerService.catchHttpErrors(() => EMPTY, { showErrorDetailsInToast: true })),
    ),
  );

  readonly isSkipping = signal(false);

  readonly isSaving$$ = new BehaviorSubject<boolean>(false);

  readonly questionConfig$ = this.mode$.pipe(map((mode) => (mode ? getQuestionReviewConfig(mode) : null)));
  readonly reviewConfig$ = this.mode$.pipe(
    map((mode) => (mode ? ({ type: 'derivative', mode } satisfies ReviewConfig) : null)),
    shareOneReplay(),
  );

  readonly state = connectState({
    creatingReviewItemError: this.questionValidationService.creatingReviewItemError$$,
    creatingReviewItem: this.questionValidationService.creatingReviewItem$$,
    permissionError: this.questionValidationService.permissionError$$,
    mode: this.mode$,
    editingOptions: this.questionConfig$,
    question: this.activeQuestion$,
    referenceQuestion: this.referenceQuestion$,
    activeItem: toObservable(this.questionEditorItemsFormService.activeQuestionItem),
    activeSubItem: toObservable(this.questionEditorItemsFormService.activeQuestionSubItem),
    isSendingFeedback: this.reviewItemService.isSendingFeedback$$,
    isSkippable: this.questionConfig$.pipe(map((config) => config?.isSkippable)),
    canStoreInBraintrust: this.questionConfig$.pipe(map((config) => config?.canStoreInBraintrust)),
    allowJsonEditor: this.questionConfig$.pipe(map((config) => config?.allowJsonEditor)),
    isSaving: this.isSaving$$,
    canUndo: this.questionEditorItemsFormService.canUndo$,
    canRedo: this.questionEditorItemsFormService.canRedo$,
    formState: toObservable(this.reviewFormState),
    isAdmin: this.userQuery.selectHasRole('ADMIN'),
    anyEditingAllowed: this.questionConfig$.pipe(map(isAnyEditingAllowed)),
    calculatorAllowed: this.questionQuery
      .selectActive()
      .pipe(map((question) => question?.question.metadata?.calculator_allowed ?? false)),
    markCalculationMethod: this.syllabusQuery.markCalculationMethod$,
  });

  undo() {
    this.questionEditorItemsFormService.undo();
  }
  redo() {
    this.questionEditorItemsFormService.redo();
  }

  saveAndContinue() {
    this.isSaving$$.next(true);

    const questionId = this.state.question?.id;
    assertNonNullable(questionId, 'questionId');

    const feedback = this.reviewFormState()?.value?.feedback;
    assertNonNullable(feedback, 'feedback');

    this.questionEditorItemsFormService
      .getProcessedFormData()
      .pipe(
        switchMap((processedData) => {
          const originalQuestionItems = this.questionQuery.getEntity(questionId)?.question.question_items;
          if (
            this.state.anyEditingAllowed &&
            (!originalQuestionItems || !isEqual(getFormData(originalQuestionItems), processedData))
          ) {
            return this.questionService.update(questionId, { question_items: processedData });
          }

          console.debug('no changes, skipping question update');

          return of(null);
        }),
        switchMap(() => this.reviewItemService.giveQuestionFeedback(questionId, feedback)),
        switchMap(() => this.deleteQuestionState(questionId)),
        switchMap(() => this.reviewConfig$.pipe(first(Boolean))),
        switchMap((reviewConfig) => this.questionValidationService.refreshReviewItem(reviewConfig)),
        this.errorHandlerService.catchHttpErrors(() => EMPTY, { showErrorDetailsInToast: true }),
        this.errorHandlerService.catchError(
          `[DerivativeQuestionValidationComponent.saveAndContinue]: failed to save feedback`,
          () => EMPTY,
          {
            toast: `Image upload failed, please contact us`,
            context: {
              questionId: this.state.question?.id,
            },
          },
        ),
        finalize(() => this.isSaving$$.next(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  async openSkipQuestionDialog() {
    const result = await this.skipQuestionDialogService.openDialog();
    if (result && !this.isSkipping()) {
      this.skipReviewItem();
    }
  }

  skipReviewItem() {
    const questionId = this.state.question?.id;
    assertNonNullable(questionId, 'questionId');

    this.isSkipping.set(true);
    this.questionValidationService
      .skipReviewItem()
      .pipe(
        switchMap(() => this.deleteQuestionState(questionId)),
        switchMap(() => this.reviewConfig$.pipe(first(Boolean))),
        switchMap((reviewConfig) => this.questionValidationService.refreshReviewItem(reviewConfig)),
        finalize(() => this.isSkipping.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onBrainTrustDatasetChange(event: Event) {
    const target = event.target as HTMLInputElement;
    if (target) {
      this.brainTrustDataset.set(target.value);
    }
  }

  private deleteQuestionState(questionId: number) {
    assertNonNullable(this.state.mode, 'mode');
    return this.questionItemsStorageService.deleteQuestionState({
      questionType: QuestionType.Derivative,
      activeMode: this.state.mode,
      activeQuestionId: questionId,
    });
  }

  private fetchQuestionOnIdChange() {
    return this.activeQuestionId$.pipe(
      filter(isNotNullish),
      switchMap((questionId) =>
        this.questionService.fetchQuestionDetails(questionId, {
          setActive: true,
          resetStore: true,
        }),
      ),
      this.errorHandlerService.catchHttpErrors(() => EMPTY, { showErrorDetailsInToast: true }),
    );
  }

  private resetFeedbackOnQuestionIdChange() {
    return this.activeQuestionId$.pipe(
      filter(isNotNullish),
      tap(() => this.reviewFormState.set(null)),
    );
  }

  private handleSyllabusChange() {
    return this.syllabusQuery.selectActiveId().pipe(
      filter(isNotNullish),
      distinctUntilChanged(),
      skip(1),
      tap(() => this.questionService.resetActiveQuestion()),
      switchMap(() => this.reviewConfig$.pipe(first(Boolean))),
      switchMap((reviewConfig) => this.questionValidationService.refreshReviewItem(reviewConfig)),
    );
  }

  private handleModeChange() {
    return this.mode$.pipe(
      filter(isNotNullish),
      tap((mode) => {
        this.questionService.resetActiveQuestion();
        this.activeModeService.setDerivativeQuestionMode(mode);
        this.questionEditorItemsFormService.mode$$.next(mode);
      }),
      switchMap((mode) =>
        this.questionValidationService.resetReviewItem({
          type: 'derivative',
          mode,
        }),
      ),
    );
  }
}
